<template>
    <page>
        <fast-table
            v-if="dataLoaded"
            recordLabel="Pay Group Runs"
            tableClasses="table-anchor--id"
            :sections="sections"
            :headers="headers"
            :data="tableData"
            :activeRowId="currentRun ? currentRun.id : null"
            @click="currentRun = $event"
        />

        <paygroup-sidebar
            v-if="currentRun"
            :client="sidebarClient"
            :run-id="currentRun.id"
            @close="currentRun = null"
        />
    </page>
</template>

<script>
import FastTable from '@/components/FastTable'
import Page from '@/components/Page'
import PaygroupSidebar from '@/pages/client/payroll/components/PaygroupSidebar'

export default {
    components: {FastTable, Page, PaygroupSidebar, },
    computed: {
        sidebarClient() {
            // PaygroupSidebar expects the payroll start screen's client object, but it
            // only uses client.id (for API calls) and client.companies (for names).
            if (!this.currentRun) {
                return null
            }
            const client = Object.assign({}, this.currentRun.company.client)
            client.companies = [this.currentRun.company]
            return client
        },
        tableData() {
            const sectionData = []

            this.paygroupRuns.forEach(run => {
                sectionData.push({
                    id: run.id,
                    object: run,
                    cells: [
                        run.company.client.client_code,
                        run.company.client.name,
                        run.company.name,
                        run.paygroup.name,
                        this.processByWarning(run),
                        '', // Alerts
                        this.statusCell(run),
                        run.process_by_time,
                        run.processed_on,
                        run.check_date,
                        this.payPeriodLabel(run),
                    ],
                })
            })

            return [sectionData]
        },
    },
    data() {
        return {
            paygroupRuns: [],
            dataLoaded: false,
            currentRun: null,
            headers: [
                {label: 'Client ID', classes: 'cw-4', isSearchable: true},
                {label: 'Client', classes: 'cell-company', isSearchable: true, isFilterable: true},
                {label: 'Company', classes: 'cell-company', isSearchable: true, isFilterable: true},
                {label: 'Pay Group', classes: 'cw-8', isSearchable: true, isFilterable: true},
                {label: 'Warnings', classes: 'cw-warning', type: 'warning', isSearchable: false},
                {label: 'Alerts', classes: 'cw-warning', isSearchable: false},
                {label: 'Status', classes: 'cw-10', isSearchable: true, isFilterable: true},
                {label: 'Process By', classes: 'cw-11h', type: 'datetime', isSearchable: false},
                // PaygroupRun.processed_on is stamped when the run completes, so it reads
                // as the approval time; empty until then
                {label: 'Approved On', classes: 'cw-11h', type: 'datetime', isSearchable: false},
                {label: 'Check Date', classes: 'cw-date', type: 'date', isSearchable: false, defaultSort: true},
                {label: 'Pay Period', classes: 'cw-12', isSearchable: false},
            ],
            sections: [
                {title: 'Processing Today', defaultIsClosed: false, id: 'processing'},
            ],
            // For this table 'completed' also displays as Approved; the checkmark
            // (statusCell) distinguishes fully-processed runs from just-approved ones.
            statusLabels: {
                'not-started': 'Not Started',
                'in-progress': 'In Progress',
                'ready-for-approval': 'Ready for Approval',
                'approved': 'Approved',
                'completed': 'Approved',
                'canceled': 'Canceled',
            },
        }
    },
    mounted() {
        this.$store.dispatch('SET_PAGE_TITLE', 'Daily Payroll / Processing Today')
        this.getRuns()
    },
    methods: {
        getRuns() {
            this.$store.dispatch('START_LOADING')
            this.$api.get('/admin/daily-payroll/processing').then(resp => {
                this.paygroupRuns = resp.paygroup_runs
                this.dataLoaded = true
                this.$store.dispatch('STOP_LOADING')
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        statusLabel(status) {
            return this.statusLabels[status] || status
        },
        processByWarning(run) {
            // TODO: UI-only fudge — shows a warning count when the run is past its
            // process-by time but not yet approved. Replace with real warning objects
            // (and their click-through/sidebar plumbing) once those exist for
            // paygroup runs.
            const pastDue = run.process_by_time && (new Date(run.process_by_time) < new Date())
            const unapproved = ['not-started', 'in-progress', 'ready-for-approval'].includes(run.status)
            return (pastDue && unapproved) ? 1 : ''
        },
        statusCell(run) {
            const cell = {
                value: this.statusLabel(run.status),
                _meta: {
                    additionalCSSClasses: ['text-bold'],
                },
            }
            if (run.status == 'approved' || run.status == 'completed') {
                cell._meta.additionalCSSClasses.push('text-approved-green')
            }
            if (run.status == 'completed') {
                cell._meta.hasCheckmark = true
            }
            return cell
        },
        payPeriodLabel(run) {
            if (!run.pay_period_start_date || !run.pay_period_end_date) {
                return ''
            }
            const startDate = this.$filters.date(run.pay_period_start_date, 'MM/DD/Y')
            const endDate = this.$filters.date(run.pay_period_end_date, 'MM/DD/Y')
            return `${startDate} - ${endDate}`
        },
    },
}
</script>

<style>
/* Unscoped: applied to cells rendered inside the FastTable Cell component */
.text-approved-green {
    color: #00A050;
}
</style>
